"use client"
import { Typography, Box, Container, Paper, useTheme } from "@mui/material"
import { motion } from "framer-motion"

// Định nghĩa kiểu dữ liệu cho FAQ
interface FAQItem {
  id: number
  question: string
  answer: string
}

// Dữ liệu FAQ
const faqData: FAQItem[] = [
  {
    id: 1,
    question: "Do I need prior AI experience?",
    answer:
      "No, the event is designed for educators at all levels of AI familiarity. Support resources will be provided to help you navigate AI tools.",
  },
  {
    id: 2,
    question: "What if I teach a niche subject?",
    answer:
      "We welcome educators from all disciplines. Your unique perspective on a specialized subject area can lead to particularly innovative solutions.",
  },
  {
    id: 3,
    question: "Will travel be covered for the Summit?",
    answer:
      "Yes, travel and accommodation expenses will be covered for all finalists invited to the July Educators Summit.",
  },
  {
    id: 4,
    question: "Can I collaborate with other educators?",
    answer:
      "While submissions must be individual, you're encouraged to discuss ideas with colleagues. The final work must represent your own insights and solutions.",
  },
]

// Component hiển thị từng mục FAQ
const FAQItem = ({ item, index }: { item: FAQItem; index: number }) => {
  const theme = useTheme()

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{ display: "flex", marginBottom: "20px" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mr: 2,
          position: "relative",
          width: "60px",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            bgcolor: "rgba(63, 81, 181, 0.1)",
            width: "60px",
            height: "80px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "15px",
              background: "rgba(63, 81, 181, 0.1)",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "15px",
              background: "rgba(63, 81, 181, 0.1)",
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px",
            },
          }}
        >
          <Typography
            variant="h4"
            component="div"
            sx={{
              fontWeight: "bold",
              color: theme.palette.primary.main,
            }}
          >
            {item.id}
          </Typography>
        </Paper>
      </Box>

      <Box sx={{ flex: 1 }}>
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
        >
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontWeight: "bold",
              color: theme.palette.primary.main,
              mb: 1,
            }}
          >
            {item.question}
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
        >
          <Typography variant="body1" color="textPrimary">
            {item.answer}
          </Typography>
        </motion.div>
      </Box>
    </motion.div>
  )
}

// Component chính
export default function FAQComponent() {
  const theme = useTheme()

  return (
    <Container maxWidth="lg" sx={{ py: 8 }} id="faq">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <Typography
          variant="h2"
          component="h1"
          align="center"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: theme.palette.primary.main,
            mb: 6,
          }}
        >
          Frequently Asked Questions
        </Typography>
      </motion.div>

      <Box>
        {faqData.map((item, index) => (
          <FAQItem key={item.id} item={item} index={index} />
        ))}
      </Box>
    </Container>
  )
}

